import { withLocale } from "@/common/utils/url.utils";
import { CANONICAL_HOST } from "@/config";

/**
 * URL shapes the search service speaks.
 *
 * Paths come back relative to the site root and mostly without a leading slash
 * ("furniture", "search?query=chair"); Home is the exception and arrives as "/".
 * Canonical URLs may already be absolute.
 */

const isAbsolute = (url: string) => /^https?:\/\//.test(url);

/** Service path -> router-ready href. */
export const toHref = (path: string) =>
  isAbsolute(path) || path.startsWith("/") ? path : `/${path}`;

export { withLocale };

/**
 * Absolute URL on the canonical domain, for the tags crawlers read (canonical,
 * hreflang, JSON-LD). Returns undefined when there is no path to build one from.
 *
 * This used to bail out when `CANONICAL_DOMAIN` was unset too, on the grounds
 * that a missing tag beats a broken page. `CANONICAL_HOST` carries a fallback,
 * so there is no longer an unset case to guard.
 */
export const toAbsoluteUrl = (
  path: string | null | undefined,
  locale?: string,
  defaultLocale?: string
) => {
  if (!path) {
    return undefined;
  }

  const href = toHref(path);

  if (isAbsolute(href)) {
    return href;
  }

  return new URL(withLocale(href, locale, defaultLocale), CANONICAL_HOST).href;
};

/**
 * `meta.page.alternateUrls` -> `{ nl: "/meubels", … }`, dropping locales the page has
 * no URL for. Feeds the header's language switcher.
 */
export const toLocalizedPaths = (
  alternateUrls: Record<string, string | null> | null | undefined
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(alternateUrls ?? {}).flatMap(([locale, path]) =>
      path ? [[locale, toHref(path)]] : []
    )
  );
